import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import TenantSheet from "@/components/TenantSheet";
import CommandPalette from "@/components/CommandPalette";
import DetailSheet, { DetailProvider } from "@/components/DetailSheet";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useTheme } from "@/hooks/useTheme";

// Per-route code splitting: each page becomes its own chunk loaded on first
// navigation. Drops the main bundle below the 500 KB warning and gives faster
// first-paint on Overview (the default landing page) — Finance/Operations/
// Audit/Clients/Marketing only fetch when navigated to.
const Overview = lazy(() => import("@/pages/Overview"));
const Finance = lazy(() => import("@/pages/Finance"));
const Operations = lazy(() => import("@/pages/Operations"));
const Marketing = lazy(() => import("@/pages/Marketing"));
const Clients = lazy(() => import("@/pages/Clients"));
const Audit = lazy(() => import("@/pages/Audit"));
const WeeklyPlan = lazy(() => import("@/pages/WeeklyPlan"));
const WIP = lazy(() => import("@/pages/WIP"));
const Settings = lazy(() => import("@/pages/Settings"));
const ClientDetail = lazy(() => import("@/pages/ClientDetail"));
const ClientDetailOverview = lazy(() =>
  import("@/pages/ClientDetail").then((m) => ({ default: m.ClientDetailOverview }))
);
const ClientDetailApprovals = lazy(() =>
  import("@/pages/ClientDetail").then((m) => ({ default: m.ClientDetailApprovals }))
);
const ClientDetailThreads = lazy(() =>
  import("@/pages/ClientDetail").then((m) => ({ default: m.ClientDetailThreads }))
);
const ClientDetailArtifacts = lazy(() =>
  import("@/pages/ClientDetail").then((m) => ({ default: m.ClientDetailArtifacts }))
);
const ClientDetailPerformance = lazy(() =>
  import("@/pages/ClientDetail").then((m) => ({ default: m.ClientDetailPerformance }))
);
const ClientDetailActivity = lazy(() =>
  import("@/pages/ClientDetail").then((m) => ({ default: m.ClientDetailActivity }))
);
const ClientDetailSettings = lazy(() =>
  import("@/pages/ClientDetail").then((m) => ({ default: m.ClientDetailSettings }))
);

function PageFallback() {
  return (
    <div className="flex items-center justify-center py-24 text-slate-400 text-sm gap-2">
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading…
    </div>
  );
}

/**
 * RouteGuard — wraps each lazy-loaded page with Suspense + ErrorBoundary,
 * keyed on path so a render error on /finance doesn't poison /audit etc.
 * Resets cleanly on every navigation.
 */
function RouteGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname}>
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

export default function App() {
  const { resolved } = useTheme();
  return (
    <DetailProvider>
      <BrowserRouter>
        {/* Outer ErrorBoundary catches any error that escapes a per-route boundary
            (e.g., crash in Layout itself). Without `key`, this stays mounted across
            route changes — a true last-line defense. */}
        <ErrorBoundary>
          <Routes>
            <Route element={<LayoutWithSheet />}>
              <Route path="/" element={<RouteGuard><Overview /></RouteGuard>} />
              <Route path="/finance" element={<RouteGuard><Finance /></RouteGuard>} />
              <Route path="/operations" element={<RouteGuard><Operations /></RouteGuard>} />
              <Route path="/marketing" element={<RouteGuard><Marketing /></RouteGuard>} />
              <Route path="/clients" element={<RouteGuard><Clients /></RouteGuard>} />
              <Route path="/clients/:slug" element={<RouteGuard><ClientDetail /></RouteGuard>}>
                <Route index element={<ClientDetailOverviewLazy />} />
                <Route path="approvals" element={<ClientDetailApprovalsLazy />} />
                <Route path="threads" element={<ClientDetailThreadsLazy />} />
                <Route path="artifacts" element={<ClientDetailArtifactsLazy />} />
                <Route path="performance" element={<ClientDetailPerformanceLazy />} />
                <Route path="activity" element={<ClientDetailActivityLazy />} />
                <Route path="settings" element={<ClientDetailSettingsLazy />} />
              </Route>
              <Route path="/audit" element={<RouteGuard><Audit /></RouteGuard>} />
              <Route path="/weekly-plan" element={<RouteGuard><WeeklyPlan /></RouteGuard>} />
              <Route path="/wip" element={<RouteGuard><WIP /></RouteGuard>} />
              <Route path="/settings" element={<RouteGuard><Settings /></RouteGuard>} />
            </Route>
          </Routes>
        </ErrorBoundary>
        <Toaster
          theme={resolved}
          position="bottom-right"
          toastOptions={{
            classNames: {
              toast: "!bg-slate-900 !border-slate-800 !text-slate-100",
              description: "!text-slate-400",
            },
          }}
        />
      </BrowserRouter>
    </DetailProvider>
  );
}

function LayoutWithSheet() {
  return (
    <>
      <Layout />
      <TenantSheet />
      <DetailSheet />
      <CommandPalette />
    </>
  );
}

// Sub-tab Suspense wrappers — module-level lazy imports above; here we just wrap.
function ClientDetailOverviewLazy() {
  return <Suspense fallback={<PageFallback />}><ClientDetailOverview /></Suspense>;
}
function ClientDetailApprovalsLazy() {
  return <Suspense fallback={<PageFallback />}><ClientDetailApprovals /></Suspense>;
}
function ClientDetailThreadsLazy() {
  return <Suspense fallback={<PageFallback />}><ClientDetailThreads /></Suspense>;
}
function ClientDetailArtifactsLazy() {
  return <Suspense fallback={<PageFallback />}><ClientDetailArtifacts /></Suspense>;
}
function ClientDetailPerformanceLazy() {
  return <Suspense fallback={<PageFallback />}><ClientDetailPerformance /></Suspense>;
}
function ClientDetailActivityLazy() {
  return <Suspense fallback={<PageFallback />}><ClientDetailActivity /></Suspense>;
}
function ClientDetailSettingsLazy() {
  return <Suspense fallback={<PageFallback />}><ClientDetailSettings /></Suspense>;
}
